import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { usePayment } from '../context/PaymentContext';

/**
 * PaymentStatusChecker Component
 * 
 * Checks for pending payment status on app reload and redirects accordingly
 */
function PaymentStatusChecker() {
  const { paymentStatus, pendingOrder, clearPendingOrder, clearPaymentStatus } = usePayment();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Only check on initial load, not on every route change
    // Skip if already on payment-related pages
    const paymentPages = ['/checkout/payment', '/payment/success', '/payment/failure'];
    if (paymentPages.includes(location.pathname)) {
      return;
    }

    // Check if there's a pending payment status
    if (paymentStatus) {
      const status = paymentStatus.status;
      const timestamp = paymentStatus.timestamp ? new Date(paymentStatus.timestamp) : null;
      
      // If status is 'initiating' or 'token_received', user might have been redirected
      // Check if it's been more than 5 minutes (payment likely timed out)
      if (timestamp) {
        const minutesAgo = (Date.now() - timestamp.getTime()) / (1000 * 60);
        
        if (minutesAgo > 5 && (status === 'initiating' || status === 'token_received')) {
          // Payment likely timed out, clear status
          console.log('Payment status timed out, clearing...');
          clearPaymentStatus();
          clearPendingOrder();
          return;
        }
      }

      // If payment failed, redirect to failure page
      if (status === 'failed') {
        console.log('Payment failed, redirecting to failure page...');
        navigate('/payment/failure', { 
          state: { 
            error: paymentStatus.error || 'Payment processing failed',
            basketId: paymentStatus.orderId 
          } 
        });
        clearPaymentStatus();
        clearPendingOrder();
      }
    }

    // Check if there's a pending order but no status (might have been interrupted)
    if (pendingOrder && !paymentStatus) {
      // Check if order is older than 30 minutes
      const orderTimestamp = pendingOrder.fullOrderData?.created_at 
        ? new Date(pendingOrder.fullOrderData.created_at) 
        : null;
      
      if (orderTimestamp) {
        const minutesAgo = (Date.now() - orderTimestamp.getTime()) / (1000 * 60);
        
        if (minutesAgo > 30) {
          // Order is too old, clear it
          console.log('Pending order is too old, clearing...');
          clearPendingOrder();
        }
      }
    }
  }, [paymentStatus, pendingOrder, location.pathname, navigate, clearPaymentStatus, clearPendingOrder]);

  // This component doesn't render anything
  return null;
}

export default PaymentStatusChecker;

